import { useState, useEffect } from 'react';
import { getScoringMatrixConfig } from '../../../utils/mockScoringData';
import { getNominationRoundYear } from '../../../utils/getNominationRoundYear';
import { getUserSectionalCommittee } from '../../../utils/getUserSectionalCommittee';
import { axiosPost } from '../../../utils/axiosPost';
import { axiosPut } from '../../../utils/axiosPut';
import { axiosGet } from '../../../utils/axiosGet';
import { devLog, devError } from '../../../utils/devLog';

import { useAdminCollections } from '../../../context/AdminCollections';
import {
  CM_PRIMARY_ID,
  ELECTION_TYPE,
  NOMINATION_STATUS,
  NOTIFICATION_DISPLAY_MS,
  cmSeLabel,
} from '../../../utils/constants';

export const useScoring = (
  sectionalCommitteesWithCmse,
  userDetails = null,
  setNotificationTitle = null,
  setNotificationMessage = null,
  committeeArray = [],
  nominationArray = [],
  candidateArray = [],
) => {
  const [showScoringModal, setShowScoringModal] = useState(false);
  const [candidates, setCandidates] = useState([]);
  const [scoringData, setScoringData] = useState({});
  const [trainingCompleted, setTrainingCompleted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [activeElectionTypeId, setActiveElectionTypeId] = useState(2);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const userCommitteeId = getUserSectionalCommittee(
    userDetails,
    committeeArray,
  );

  const { coiDeclarationArray, triggerCandidateScoresRefresh } =
    useAdminCollections();

  const canScoreMultipleTypes = userCommitteeId === CM_PRIMARY_ID;

  const showNotification = (title, message) => {
    setNotificationTitle?.(title);
    setNotificationMessage?.(message);
    setTimeout(() => {
      setNotificationMessage?.('');
      setNotificationTitle?.('');
    }, NOTIFICATION_DISPLAY_MS);
  };

  const notifyUnsavedChanges = () => {
    setTimeout(() => {
      const validationErrors = validateScores();
      if (Object.keys(validationErrors).length === 0) {
        showNotification(
          'Unsaved Changes',
          "You have unsaved changes. Don't forget to save your work.",
        );
      }
    }, 0);
  };

  useEffect(() => {
    setActiveElectionTypeId(canScoreMultipleTypes ? 1 : 2);
  }, [canScoreMultipleTypes]);

  useEffect(() => {
    if (
      userDetails?.id &&
      nominationArray.length > 0 &&
      candidateArray.length > 0
    ) {
      loadScoringData();
      loadTrainingStatus();
    }
  }, [
    activeElectionTypeId,
    userDetails?.id,
    nominationArray.length,
    candidateArray.length,
    coiDeclarationArray,
  ]);

  const getScoreMapping = (electionType, declaration) => {
    const mappings = {
      1: {
        scientificExcellence: declaration.scientificExcellence || 0,
        internationalProfile: declaration.internationalProfile || 0,
        connectionToAustralian: declaration.connectionToAustralian || 0,
      },
      2: {
        scientificLeadership: declaration.scientificLeadership || 0,
        nationalProfile: declaration.nationalProfile || 0,
        leadershipMentorship: declaration.leadershipMentorship || 0,
      },
      3: {
        serviceToScience: declaration.serviceToScience || 0,
        nationalProfile: declaration.nationalProfile || 0,
        academyVision: declaration.academyVision || 0,
      },
    };
    return mappings[electionType] || {};
  };

  const getCandidatesFromCommittee = () => {
    if (!nominationArray.length || !candidateArray.length) {
      return [];
    }

    const relevantNominations = nominationArray.filter((nomination) => {
      const isShortlisted =
        nomination.attributes.nominationStatusTypeId ===
          NOMINATION_STATUS.SUBMITTED ||
        nomination.attributes.nominationStatusTypeId ===
          NOMINATION_STATUS.SHORTLISTED ||
        nomination.attributes.nominationStatusTypeId ===
          NOMINATION_STATUS.RECOMMENDED;

      if (!isShortlisted) return false;

      if (userCommitteeId === CM_PRIMARY_ID) {
        if (activeElectionTypeId === 1) {
          return (
            nomination.attributes.electionTypeId ===
            ELECTION_TYPE.CORRESPONDING_MEMBER
          );
        }
        if (activeElectionTypeId === 3) {
          return (
            nomination.attributes.electionTypeId ===
            ELECTION_TYPE.SPECIAL_ELECTION
          );
        }
      }

      return nomination.attributes.sectionalCommitteeId === userCommitteeId;
    });

    const relevantNominationsCandidates = relevantNominations
      .map((nomination) => {
        const candidate = candidateArray.find(
          (c) => c.id === nomination.attributes.candidateId,
        );

        if (!candidate) return null;

        const currentRound = getNominationRoundYear(new Date());

        const hasConflict = coiDeclarationArray.some(
          (coi) =>
            coi.attributes.candidateId === candidate.id &&
            coi.attributes.userId === userDetails?.id &&
            coi.attributes.nominationRound === currentRound &&
            coi.attributes.coiTypes?.data?.some(
              (coiType) => coiType.attributes.category === 'direct',
            ),
        );

        return {
          candidateId: candidate.id,
          nominationId: nomination.id,
          name: `${candidate.attributes.name} ${candidate.attributes.lastName}`,
          sectionalCommitteeId: nomination.attributes.sectionalCommitteeId,
          electionTypeId: nomination.attributes.electionTypeId,
          nominationStatusTypeId: nomination.attributes.nominationStatusTypeId,
          hasConflict,
        };
      })
      .filter(Boolean);

    return relevantNominationsCandidates.sort((a, b) =>
      a.name.localeCompare(b.name),
    );
  };

  const loadScoringData = async () => {
    setIsLoading(true);
    try {
      const realCandidates = getCandidatesFromCommittee();
      setCandidates(realCandidates);

      const existingScores = {};

      try {
        const response = await axiosGet(
          `candidate-scores?filters[userId][$eq]=${userDetails?.id}&filters[electionTypeId][$eq]=${activeElectionTypeId}`,
        );

        if (response?.data?.data && response.data.data.length > 0) {
          response.data.data.forEach((declaration) => {
            const { attributes } = declaration;
            existingScores[attributes.candidateId] = {
              ...getScoreMapping(activeElectionTypeId, attributes),
              totalScore: attributes.totalScore,
              comments: attributes.comments,
              isCompleted: attributes.isCompleted,
            };
          });
        }
      } catch (error) {
        devError('Error fetching from Strapi:', error);
      }

      setScoringData(existingScores);
    } catch (error) {
      showNotification(
        'Error',
        'Failed to load scoring data. Please try again.',
      );
    } finally {
      setIsLoading(false);
    }
  };

  const loadTrainingStatus = async () => {
    try {
      if (!userDetails?.id) return;

      const currentRound = getNominationRoundYear(new Date());

      try {
        const response = await axiosGet(
          `bias-trainings?filters[userId][$eq]=${userDetails.id}&filters[nominationRound][$eq]=${currentRound}`,
        );

        if (response?.data?.data && response.data.data.length > 0) {
          const [trainingRecord] = response.data.data;
          setTrainingCompleted(trainingRecord.attributes.isCompleted);
        }
      } catch (error) {
        devError('Error fetching training status from Strapi:', error);
      }
    } catch (error) {
      devError('Error loading training status:', error);
    }
  };

  const handleStartScoring = () => {
    setShowScoringModal(!trainingCompleted);
  };

  const handleTrainingComplete = async () => {
    try {
      const currentRound = getNominationRoundYear(new Date());
      const completedAt = new Date().toISOString();

      let existingTraining = null;
      try {
        const existingResponse = await axiosGet(
          `bias-trainings?filters[userId][$eq]=${userDetails?.id}&filters[nominationRound][$eq]=${currentRound}`,
        );

        if (existingResponse?.data?.data?.length > 0) {
          [existingTraining] = existingResponse.data.data;
        }
      } catch (error) {
        devLog('No existing training record found, will create new one');
      }

      const trainingData = {
        userId: userDetails?.id,
        isCompleted: true,
        completedAt,
        nominationRound: currentRound.toString(),
      };

      const payload = { data: trainingData };

      if (existingTraining) {
        await axiosPut(`bias-trainings/${existingTraining.id}`, payload);
      } else {
        await axiosPost('bias-trainings', payload);
      }

      setTrainingCompleted(true);

      showNotification(
        'Training Complete',
        'Unconscious bias training completed successfully.',
      );
    } catch (error) {
      devError('Error saving training completion:', error);
      showNotification(
        'Error',
        'Failed to record training completion. Please try again.',
      );
    }
  };

  const handleCommentsChange = (candidateId, comments) => {
    setScoringData((prev) => ({
      ...prev,
      [candidateId]: {
        ...prev[candidateId],
        comments,
      },
    }));

    setHasUnsavedChanges(true);
    notifyUnsavedChanges();
  };

  const calculateTotalScore = (scores) =>
    getScoringMatrixConfig(activeElectionTypeId).criteria.reduce(
      (total, criterion) => total + (scores[criterion.key] || 0),
      0,
    );

  const isValidCandidate = (candidateId) => {
    const candidateScores = scoringData[candidateId] || {};
    const matrixConfig = getScoringMatrixConfig(activeElectionTypeId);

    const totalScore = calculateTotalScore(candidateScores);

    if (totalScore === 0) {
      return false;
    }

    for (const criterion of matrixConfig.criteria) {
      const score = candidateScores[criterion.key] || 0;
      const maxLimit = criterion.adjustedMax || criterion.max;

      if (score < criterion.min || score > maxLimit) {
        return false;
      }
    }

    const hasAdjustedScore = matrixConfig.criteria.some((criterion) => {
      const score = candidateScores[criterion.key] || 0;
      return criterion.adjustedMax !== undefined && score > criterion.max;
    });

    if (hasAdjustedScore) {
      const comments = candidateScores.comments || '';
      if (comments.trim() === '') {
        return false;
      }
    }

    return totalScore <= 100;
  };

  const validateScores = () => {
    const errors = {};
    const matrixConfig = getScoringMatrixConfig(activeElectionTypeId);

    const getErrorMessage = (criterion) => {
      const hasAdjustment = criterion.adjustedMax !== undefined;

      if (hasAdjustment) {
        return `must be between ${criterion.min} and ${criterion.max}, if adjusted between ${criterion.min} and ${criterion.adjustedMax}`;
      }
      return `must be between ${criterion.min} and ${criterion.max}`;
    };

    candidates.forEach((candidate) => {
      if (candidate.hasConflict) return;

      const candidateScores = scoringData[candidate.candidateId] || {};
      const candidateErrors = [];

      matrixConfig.criteria.forEach((criterion) => {
        const maxLimit = criterion.adjustedMax || criterion.max;
        const score = candidateScores[criterion.key] || 0;
        if (score < criterion.min || score > maxLimit) {
          candidateErrors.push(
            `${criterion.label}: ${getErrorMessage(criterion)}`,
          );
        }
      });

      const totalScore = calculateTotalScore(candidateScores);

      if (totalScore === 0) {
        candidateErrors.push(
          'Total score cannot be 0. Please enter scores for the criteria.',
        );
      } else if (totalScore > 100) {
        candidateErrors.push(
          `Total cannot be greater than 100 (currently ${totalScore})`,
        );
      }

      const hasAdjustedScore = matrixConfig.criteria.some((criterion) => {
        const score = candidateScores[criterion.key] || 0;
        return criterion.adjustedMax !== undefined && score > criterion.max;
      });

      if (hasAdjustedScore) {
        const comments = candidateScores.comments || '';
        if (comments.trim() === '') {
          candidateErrors.push(
            'Comments are required when using adjusted scores (scores above the base maximum).',
          );
        }
      }

      if (candidateErrors.length > 0) {
        errors[candidate.candidateId] = candidateErrors;
      }
    });

    return errors;
  };

  const handleScoreChange = (candidateId, criterion, value) => {
    setScoringData((prev) => {
      const updatedScores = {
        ...prev[candidateId],
        [criterion]: parseInt(value) || 0,
      };
      return {
        ...prev,
        [candidateId]: {
          ...updatedScores,
          totalScore: calculateTotalScore(updatedScores),
        },
      };
    });

    setHasUnsavedChanges(true);
    notifyUnsavedChanges();
  };

  const handleSaveScoring = async () => {
    try {
      setIsLoading(true);
      const currentRound = getNominationRoundYear(new Date());
      const savedCandidates = [];

      for (const candidate of candidates) {
        if (!candidate.hasConflict && isValidCandidate(candidate.candidateId)) {
          const candidateScores = scoringData[candidate.candidateId];
          if (candidateScores) {
            let existingDeclaration = null;
            try {
              const existingResponse = await axiosGet(
                `candidate-scores?filters[candidateId][$eq]=${candidate.candidateId}&filters[userId][$eq]=${userDetails?.id}&filters[electionTypeId][$eq]=${activeElectionTypeId}`,
              );

              if (existingResponse?.data?.data?.length > 0) {
                [existingDeclaration] = existingResponse.data.data;
              }
            } catch (error) {
              devLog('No existing declaration found, will create new one');
            }

            const declaration = {
              candidateId: candidate.candidateId,
              userId: userDetails?.id,
              nominationId: candidate.nominationId,
              sectionalCommitteeId: candidate.sectionalCommitteeId,
              nominationRound: currentRound,
              electionTypeId: activeElectionTypeId,
              ...candidateScores,
              isCompleted: true,
            };

            const payload = {
              data: declaration,
            };
            let response;
            if (existingDeclaration) {
              response = await axiosPut(
                `candidate-scores/${existingDeclaration.id}`,
                payload,
              );
              setHasUnsavedChanges(false);
            } else {
              response = await axiosPost('candidate-scores', payload);
              setHasUnsavedChanges(false);
            }

            if (response?.status === 200) {
              triggerCandidateScoresRefresh(response.data.data.id);
              savedCandidates.push(candidate.name);
            }
          }
        }
      }

      setShowScoringModal(false);

      if (savedCandidates.length === 0) {
        showNotification(
          'No Scores Saved',
          'No valid scores to save. Ensure candidates have complete scores (totaling ≤100) and any required comments.',
        );
      } else if (savedCandidates.length === 1) {
        showNotification(
          'Scoring Saved',
          `Scores saved for: ${savedCandidates[0]}`,
        );
      } else {
        const candidateList = savedCandidates.join(', ');
        showNotification(
          'Scoring Saved',
          `Scores saved for ${savedCandidates.length} candidates: ${candidateList}`,
        );
      }

      return true;
    } catch (error) {
      devError('Error saving scores:', error);
      showNotification(
        'Save Failed',
        'Failed to save scoring data. Please try again.',
      );
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancelScoring = () => {
    setShowScoringModal(false);
    loadScoringData();
  };

  const getCommitteeInfo = () => {
    const committee = sectionalCommitteesWithCmse.find(
      (item) => item.id === userCommitteeId,
    );

    if (userCommitteeId === CM_PRIMARY_ID) {
      return { id: userCommitteeId, name: cmSeLabel };
    }

    return { id: userCommitteeId, name: committee?.attributes.name };
  };

  const getScoringMatrix = () => getScoringMatrixConfig(activeElectionTypeId);

  const getAvailableElectionTypes = () =>
    canScoreMultipleTypes
      ? [
          { id: 1, name: 'Corresponding Membership', type: 'corresponding' },
          { id: 3, name: 'Special Election', type: 'special' },
        ]
      : [{ id: 2, name: 'Ordinary election', type: 'sectional' }];

  return {
    showScoringModal,
    candidates,
    scoringData,
    trainingCompleted,
    isLoading,
    activeElectionTypeId,
    canScoreMultipleTypes,
    userCommitteeId,
    hasUnsavedChanges,
    handleStartScoring,
    handleTrainingComplete,
    handleScoreChange,
    handleCommentsChange,
    handleSaveScoring,
    handleCancelScoring,
    setActiveElectionTypeId,
    getCommitteeInfo,
    getScoringMatrix,
    validateScores,
    calculateTotalScore,
    getAvailableElectionTypes,
    isValidCandidate,
  };
};
